import { useState } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from './useAuth'
import { LOGO_UPLOAD_CONFIG } from '../utils/constants'

export const useLogoUpload = () => {
  const { isAdmin } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const validateFile = (file: File): string | null => {
    if (!(LOGO_UPLOAD_CONFIG.ALLOWED_TYPES as readonly string[]).includes(file.type)) {
      return 'Invalid file type. Please upload PNG, JPG, SVG, or WebP images.'
    }
    if (file.size > LOGO_UPLOAD_CONFIG.MAX_FILE_SIZE) {
      return 'File size too large. Maximum size is 2MB.'
    }
    return null
  }

  const uploadLogo = async (file: File): Promise<string | null> => {
    if (!isAdmin) {
      setError('Only admins can upload logos')
      return null
    }

    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return null
    }

    setUploading(true)
    setError(null)

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `logo-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = fileName

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(LOGO_UPLOAD_CONFIG.STORAGE_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(LOGO_UPLOAD_CONFIG.STORAGE_BUCKET)
        .getPublicUrl(filePath)

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL')
      }

      return urlData.publicUrl
    } catch (err: any) {
      setError(err.message || 'Upload failed')
      return null
    } finally {
      setUploading(false)
    }
  }

  const deleteLogo = async (logoUrl: string): Promise<boolean> => {
    if (!isAdmin) {
      setError('Only admins can delete logos')
      return false
    }

    try {
      setError(null)
      
      // Extract file path from URL
      // URL format: https://project.supabase.co/storage/v1/object/public/logos/filename
      const urlParts = logoUrl.split('/')
      const fileName = urlParts[urlParts.length - 1]
      const filePath = fileName.split('?')[0] // Remove query params if any

      const { error: deleteError } = await supabase.storage
        .from(LOGO_UPLOAD_CONFIG.STORAGE_BUCKET)
        .remove([filePath])

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      setError(err.message || 'Delete failed')
      return false
    }
  }

  const listUploadedLogos = async (): Promise<string[]> => {
    if (!isAdmin) return []

    try {
      setError(null)
      
      const { data, error: listError } = await supabase.storage
        .from(LOGO_UPLOAD_CONFIG.STORAGE_BUCKET)
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        })

      if (listError) throw listError

      return (data || []).map(file => {
        const { data: urlData } = supabase.storage
          .from(LOGO_UPLOAD_CONFIG.STORAGE_BUCKET)
          .getPublicUrl(file.name)
        return urlData.publicUrl
      })
    } catch (err: any) {
      setError(err.message || 'Failed to list logos')
      return []
    }
  }

  return {
    uploadLogo,
    deleteLogo,
    listUploadedLogos,
    uploading,
    error
  }
}
